import { FC, ReactElement, ReactNode } from 'react'

import { SpaceType } from '@tribeplatform/gql-client/types'
import {
  AdditionalContainerProps,
  ContainerSize,
} from '@tribeplatform/react-ui-kit/Layout'

import { SlateContextProps } from './context.types.js'
import { ObjectQuery } from './query.types.js'

export enum BlockType {
  Block = 'BLOCK',
  BuildingBlock = 'BUILDING_BLOCK',
  Layout = 'LAYOUT',
}

export type RegisteredBlockProps<T extends UnknownProps> = T & {
  // These props are added by the slate kit
  className?: string
  'data-block-id'?: string
  'data-block-name'?: string
  /**
   * `direct` is a property for **Login** block extracted and becomes `true` when
   * accessing **\/auth/login/direct**, it is `undefined` otherwise.
   */
  direct?: true
  /**
   * `invitationLinkId` is a route parameter from **\/auth/join/:id** used by SignUp block
   */
  invitationLinkId?: string
}

export type BlockSize = ContainerSize

export type UnknownProps = Record<string, unknown>

export type BC<P extends UnknownProps> = FC<RegisteredBlockProps<P>>
export type BSC<P extends UnknownProps> = FC<
  Omit<Omit<P, 'pushSettings'>, 'popSettings'> & {
    pushSettings: <K extends UnknownProps>(
      title: string,
      Component: BSC<K>,
      props?: Partial<K>,
    ) => void
    popSettings: () => void
    deactivateBlock: () => void
  }
>

export type LayoutBlockProps = UnknownProps & {
  containerProps?: AdditionalContainerProps
  /**
   * Layout blocks cannot be wrapped with an extra div
   * because the children, for example container inside container,
   * assumes to be direct children of the parent container and
   * sets it's layout accordingly.
   * So we pass the highlight ring and add block buttons component
   * as a prop to the layout block.
   */
  HighlightWrapper?: ReactNode
}
export type LBC<P extends LayoutBlockProps> = BC<P>

export type UnknownBlockComponent = BC<UnknownProps>

export type BlockMeta = {
  releaseDate?: string
  releaseMessage?: string
  deprecationMessage?: string
}

export type BlockDynamicConfig = {
  hide?: boolean
  displayName?: string
  selectedState?: string
  /**
   * Block unmounts itself when its emptiness check returns true.
   * Each block defines what "empty" means for it.
   */
  hideWhenEmpty?: boolean
  /**
   * Blocks sharing the same id reveal together — none renders fully
   * until every member's loading is done. Generic; first user is the
   * Nexus Posts pinned twin + its main sibling.
   */
  revealInPairId?: string
  /**
   * Per-instance Nexus-editor control overrides. Each suppresses one
   * BlockActions button when `false` (in nexus mode only — Pro DS
   * ignores them). The mapping of "which controls a derived block gets"
   * lives in the domain factory that stamps these flags.
   */
  nexusEditable?: boolean
  nexusRemovable?: boolean
  nexusHidable?: boolean
}

export type BlockContextAwareConfig = {
  displayName?: string
  description?: string
}

export type BlockAvailabilityConditions = {
  size: BlockSize
  spaceType: SpaceType
  header: boolean
}

export type BlockConfig<P extends UnknownProps> = BlockContextAwareConfig &
  BlockDynamicConfig & {
    type: BlockType
    usable?: boolean
    availabilityConditions?: ObjectQuery<BlockAvailabilityConditions>
    deprecated?: boolean
    removable?: boolean
    editable?: boolean
    copyDisabled?: boolean
    acceptsChildren?: boolean
    lockedChildren?: boolean
    states?: string[]
    /**
     * Whether to hide the add block buttons in the block overlay
     * in design mode.
     */
    hideAddBlockButtons?: boolean

    Settings?: BSC<UnknownProps>
    fullSidebarSettings?: boolean
    availableInNexus?: boolean
    /**
     * If true, this widget is Nexus-only — it never appears in the Pro
     * editor's add-block picker. Pair with `availableInNexus: true` for
     * widgets that exist solely as a Nexus editor identity (e.g.
     * SpacePromotion rebrands to Posts at slate-emit time).
     */
    nexusOnly?: boolean
    /** If false, this widget never appears in the Add Widget picker (Nexus mode). */
    nexusAddable?: boolean
    /** If false, this widget cannot be removed or copied in the Nexus editor (e.g. SpaceHeader, Posts). */
    nexusRemovable?: boolean
    /**
     * If true, only one instance of this widget type is allowed per zone.
     * The Add Widget picker hides it once an instance already exists in the target zone.
     */
    nexusSingleton?: boolean
    /**
     * Zone-scoped variant of `nexusSingleton`: only one instance is allowed in
     * each listed zone (picker hides it there once an instance exists, and the
     * row menu's Copy is disabled). Unlisted zones stay unrestricted — e.g.
     * Members allows one directory in 'main' but any number in 'sidebar'.
     */
    nexusSingletonZones?: readonly ('main' | 'sidebar')[]
    /**
     * If set, this widget only appears in the Add Widget picker for the listed zones.
     * Omit to allow in all zones.
     */
    allowedNexusZones?: readonly ('main' | 'sidebar')[]
    /**
     * Zone-keyed template allow-list: within each listed zone, the widget only
     * appears in the Add Widget picker for spaces whose
     * NexusSpaceConfig.templateId is listed (spaces without a templateId never
     * offer it there). Zones not listed stay unrestricted — e.g. Members uses
     * `{ main: ['member-directory'] }` so the main-zone directory is exclusive
     * to Member Directory spaces while the sidebar widget stays addable
     * anywhere. Picker-only — templates can still seed the widget anywhere.
     */
    allowedNexusTemplatesByZone?: Partial<
      Record<'main' | 'sidebar', readonly string[]>
    >
    /**
     * Template deny-list: the widget is hidden from the Add Widget picker in
     * spaces whose NexusSpaceConfig.templateId is listed (e.g. Posts is
     * excluded from 'member-directory', which has no post type). Spaces
     * without a templateId are unaffected.
     */
    excludedNexusTemplates?: readonly string[]
    NexusSettings?: BSC<UnknownProps>
    /** If true, the widget display name title is hidden in the Nexus settings panel. */
    nexusSettingsHideTitle?: boolean
    Icon?: ReactElement
    defaultProps?: Partial<P> | undefined
    initialProps?: P
  }

export type RegisteredBlockConfig<P extends UnknownProps> = BlockConfig<P> & {
  getContextAwareConfig?: (options: {
    context: SlateContextProps
    props?: P
    compiledProps?: P
  }) => Partial<BlockContextAwareConfig>
}

export type RegisteredBlock<P extends UnknownProps> = {
  recommendedName: string
  Component: BC<P>
  meta?: BlockMeta
  config: RegisteredBlockConfig<P>
}
export type RegisteredBlockWithName<P extends UnknownProps> =
  RegisteredBlock<P> & {
    name: string
  }

export type RegisteredBlocks = Record<
  string,
  RegisteredBlock<Record<string, unknown>>
>
export type RegisteredBlocksWithNames = Record<
  string,
  RegisteredBlockWithName<Record<string, unknown>>
>

export type BlockWrapperComponent = FC<{
  Block: UnknownBlockComponent
  childrenBlocks: ReactElement[]
  children: ReactElement | string
  onAddBlock?: (position: AddBlockPosition) => void
}>

export type CompiledBlock<P extends UnknownProps> = {
  id: string
  parentId: string
  name: string
  Component: BC<P>
  props: P
  extraProps: BlockDynamicConfig
  compiledProps: P
  config: BlockConfig<P>
  output: UnknownProps
  children: string[]
}

export type UnknownCompiledBlock = CompiledBlock<UnknownProps>

export type BlockQuery<P extends UnknownProps> = ObjectQuery<
  Omit<BlockConfig<P>, 'availabilityConditions'>
>

export enum AddBlockPosition {
  Before = 'before',
  After = 'after',
}
